/**
 * Discord OAuth2 popup helper. Opens the Discord `identify`-scope consent popup
 * and resolves with the returned `authorization_code`, delivered from the popup
 * back to this opener via `postMessage` (the popup runs our own
 * `/discord-oauth-callback` page, which relays the code and closes itself).
 *
 * There is no generic OAuth-popup helper in this codebase; this is modeled on
 * the same gesture constraint documented for `signInWithPopup` in
 * auth-service.ts — the popup MUST be opened synchronously inside the click
 * handler, before any await, or the browser blocks it. So `openDiscordOAuthPopup`
 * opens the window as its very first statement and returns a Promise.
 *
 * The `redirect_uri` here MUST byte-for-byte match the api-server's
 * `DISCORD_OAUTH_REDIRECT_URI` (used in the token exchange) and a registered
 * redirect on the Discord application, or the exchange is rejected.
 */

const DISCORD_AUTHORIZE_URL = 'https://discord.com/api/oauth2/authorize';
const POPUP_TIMEOUT_MS = 120_000;

export class DiscordOAuthError extends Error {}

interface DiscordOAuthMessage {
  type?: string;
  code?: string;
  state?: string;
  error?: string;
}

export function isDiscordOAuthConfigured(): boolean {
  return Boolean(
    import.meta.env.VITE_DISCORD_CLIENT_ID && import.meta.env.VITE_DISCORD_OAUTH_REDIRECT_URI
  );
}

/**
 * Open the Discord consent popup and resolve with the `authorization_code`.
 * Rejects if popups are blocked, the user cancels, config is missing, or it
 * times out. Call this SYNCHRONOUSLY as the first statement of the click
 * handler (no await before it).
 */
export function openDiscordOAuthPopup(): Promise<string> {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_DISCORD_OAUTH_REDIRECT_URI;
  if (!clientId || !redirectUri) {
    return Promise.reject(new DiscordOAuthError('Discord sign-in is not configured'));
  }

  // Random state binds the popup's response to this exact request (CSRF guard).
  const state = crypto.randomUUID();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'identify',
    state,
  });

  // Open synchronously — must be the first thing the click handler does (see
  // file header). A blocked popup returns null.
  const popup = window.open(
    `${DISCORD_AUTHORIZE_URL}?${params.toString()}`,
    'discord-oauth',
    'width=500,height=800'
  );
  if (!popup) {
    return Promise.reject(new DiscordOAuthError('Popup blocked. Allow popups and retry.'));
  }

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(closedPoll);
      clearTimeout(timeout);
    };
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      // Only trust our own origin (the callback page runs on our domain).
      if (event.origin !== window.location.origin) return;
      const data = event.data as DiscordOAuthMessage;
      if (data?.type !== 'discord-oauth' || data.state !== state) return;
      popup.close();
      if (data.error || !data.code) {
        // Normalize a user "Cancel" on the consent screen (Discord sends
        // error=access_denied / a verbose "denied" description) into the same
        // cancellation message as closing the popup, so the caller can swallow
        // both with one /cancel/i check instead of surfacing raw OAuth jargon.
        const denied = data.error === 'access_denied' || /denied/i.test(data.error ?? '');
        const message = denied
          ? 'Discord sign-in was cancelled'
          : data.error || 'Discord sign-in failed';
        settle(() => reject(new DiscordOAuthError(message)));
      } else {
        const code = data.code;
        settle(() => resolve(code));
      }
    };
    window.addEventListener('message', onMessage);

    // If the user closes the popup without granting, reject so the button resets.
    const closedPoll = setInterval(() => {
      if (popup.closed) {
        settle(() => reject(new DiscordOAuthError('Discord sign-in was cancelled')));
      }
    }, 500);

    const timeout = setTimeout(() => {
      popup.close();
      settle(() => reject(new DiscordOAuthError('Discord sign-in timed out')));
    }, POPUP_TIMEOUT_MS);
  });
}
